import * as React from 'react';
import { connect } from 'react-redux';
import { Trans, withTranslation } from 'react-i18next';
import * as R from 'ramda';

import Tabs from '@common/components/tabs';
import Container from '@common/components/container';
import { Overview, OverviewContent } from '@common/components/overview';
import Bar from '@common/components/bar';
import { Select } from '@common/components/form/inputs/select';
import Spinner from '@common/components/spinner';
import Icon from '@common/components/icon';
import Tooltip from '@common/components/tooltip';
import { AreaChart, BarChart } from '@common/components/chart';
import { pageWrapper, EEventNames } from '../../../../../client/analytics';
import ChartTooltip from '../../components/tooltip';
import DateRangeSelector, { getDates } from '../../components/date-range-selector';
import { EMetricTypes } from '../../../../common/definitions';
import * as organisationSelector from '../../../organisation/selectors/organisation';

const { Row } = Overview.Content;

const getIncreaseStatus = (increase) => {
  if (increase > 0) return 'positive';
  if (increase < 0) return 'negative';

  return 'default';
};

const StatisticsTabsHeaderItem = ({
  title, tooltip, count, oldCount, percentage, onClick, active,
}) => {
  let increase;

  if (count !== undefined && oldCount) {
    increase = (count > oldCount
      ? (count / oldCount) * 100 - 100
      : (100 - 100 / (oldCount / count)) * -1);
  }

  return (
    <div className={`Tabs__Header__OverviewItem${active ? ' Tabs__Header__OverviewItem--active' : ''}`} onClick={onClick}>
      <div className="Tabs__Header__OverviewItem__Title">
        {title}
        {tooltip && <Tooltip title={tooltip} placement="bottom"><Icon type="help" /></Tooltip>}
      </div>
      <div className="Tabs__Header__OverviewItem__Count">
        {count && count.toLocaleString()}
        {percentage !== undefined && (count !== undefined
          ? <small><Trans
              i18nKey="network:analytics_percentage_of_registered"
              values={{ count: percentage && `${percentage.toFixed(1)}%` }}
              components={[<b>a</b>]}
          /></small>
          : (
            <span>
              {percentage.toFixed(1)}
              %
            </span>
          )
        )}
      </div>
      {(increase !== undefined && (
        <div className="Tabs__Header__OverviewItem__Increase">
          <span
            className={`
              Tabs__Header__OverviewItem__IncreaseCount 
              Tabs__Header__OverviewItem__IncreaseCount--status-${getIncreaseStatus(increase)}
            `}
          >
            {increase !== 0 && <Icon type={increase > 0 ? 'arrow_drop_up' : 'arrow_drop_down'} />}
            {increase.toFixed(1)}
            %
          </span>
          &nbsp;
          <Trans i18nKey="network:analytics_compared_vs_past_period" />
        </div>
      )) || (
        <div className="Tabs__Header__OverviewItem__Increase">&nbsp;</div>
      )}
    </div>
  );
};

class StatisticsEmployeesContainer extends React.Component {

  constructor(props) {
    super();

    this.mounted = true;
    this.state = {
      isInitialising: !!props.analytics[EMetricTypes.USERS_ACTIVE],
      isFetching: true,
      selectedNetwork: props.match.params.networkId || false,
      selectedOrder: false,
      selectedBars: {
        [EMetricTypes.USERS_REGISTERED]: true,
        [EMetricTypes.USERS_ACTIVE]: true,
      },
      selectedDateValue: 2,
      dates: getDates(2),
    };

    this.handleSetDateRange = this.handleSetDateRange.bind(this);
    this.handleChangeSelectedNetwork =
      (option) => this.setState({ selectedNetwork: option.id }, this.handleFetchData);
    this.handleChangeOrder = (option) => this.setState({ selectedOrder: option.value });
    this.handleToggleBar = (dataKey) => this.setState({
      selectedBars: { ...this.state.selectedBars, [dataKey]: !this.state.selectedBars[dataKey] },
    });
  }

  UNSAFE_componentWillMount() {
    this.handleFetchData();
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  static props;

  async handleFetchData() {
    const { dates: [current, comparison] } = this.state;

    this.setState({ isFetching: true });

    await Promise.all([
      this.props.fetchAnalyticsCounts(
        [EMetricTypes.USERS_INVITED, EMetricTypes.USERS_REGISTERED, EMetricTypes.USERS_PENDING],
        this.state.selectedNetwork,
      ),
      this.props.fetchAnalytics(
        [EMetricTypes.USERS_ACTIVE, EMetricTypes.USERS_INVITED, EMetricTypes.USERS_REGISTERED],
        current.start,
        current.end,
        comparison.start,
        comparison.end,
        this.state.selectedNetwork,
      ),
      this.props.fetchAnalytics(
        [EMetricTypes.USERS_ACTIVE, EMetricTypes.USERS_INVITED, EMetricTypes.USERS_REGISTERED],
        current.start,
        current.end,
        comparison.start,
        comparison.end,
        this.state.selectedNetwork,
        'employees.group',
      ),
    ]);

    if (!this.mounted) return;

    this.setState({ isInitialising: false, isFetching: false });
  }

  handleSetDateRange({ value, dates }) {
    this.setState({
      selectedDateValue: value,
      dates,
    }, this.handleFetchData);
  }

  render() {
    const {
      isInitialising, isFetching, selectedNetwork, selectedOrder, selectedDateValue, selectedBars,
    } = this.state;
    const { match: { params }, analytics, networks, t } = this.props;

    const getTimeseries = (key) => (analytics.data[key] ? analytics.data[key].timeseries : []);
    const getGroupValues = (key) => (analytics.data[key] ? analytics.data[key].values : []);
    const getTotal = (key) => (analytics.data[key] ? analytics.data[key].total : 0);
    const getOldTotal = (key) => (analytics.data[key] ? analytics.data[key].old_total : 0);
    const getCount = (key) => (analytics.counts[key] ? analytics.counts[key].total : 0);

    const getValues = (tint) => [{
      dataKey: 'old_count',
      label: t('network:analytics_past_period'),
    }, {
      dataKey: 'current_count',
      label: t('network:analytics_selected_period'),
      stroke: tint,
    }];

    const loading = isInitialising || isFetching || (
      Object.keys(analytics.data).length <= 0 ||
      Object.keys(analytics.counts).length <= 0
    );

    const getAdoptionTimeseries = () => {
      const registeredTimeseries = getTimeseries(EMetricTypes.USERS_REGISTERED);

      return getTimeseries(EMetricTypes.USERS_INVITED)
        .map((row, i) => ({
          ...row,
          current_count: row.current_count !== null
            ? (registeredTimeseries[i].current_count / row.current_count) * 100
            : null,
          old_count: row.old_count !== null
            ? (registeredTimeseries[i].old_count / row.old_count) * 100
            : null,
        }));
    };

    const getNetworkNames = (data) => data.map((metric) => ({
      [EMetricTypes.USERS_ACTIVE]: (
        metric[EMetricTypes.USERS_ACTIVE] && metric[EMetricTypes.USERS_INVITED] &&
        ((metric[EMetricTypes.USERS_ACTIVE] / metric[EMetricTypes.USERS_INVITED]) * 100)
      ) || 0,
      [EMetricTypes.USERS_REGISTERED]: (
        metric[EMetricTypes.USERS_REGISTERED] && metric[EMetricTypes.USERS_INVITED] &&
        ((metric[EMetricTypes.USERS_REGISTERED] / metric[EMetricTypes.USERS_INVITED]) * 100)
      ) || 0,
      name: (R.find(R.propEq('id', metric.network_id), networks) || { name: '-' }).name,
    }));

    return (
      <Container name="Statistics">
        <Container.Content>
          <Bar>
            <h2>
              <Trans i18nKey="network:analytics_employees" />
            </h2>
            {!params.networkId && (
              <div className="pull-right">
                <Select
                  searchable
                  clearable={false}
                  value={selectedNetwork}
                  valueKey="id"
                  labelKey="name"
                  options={[
                    { id: false, name: t('network:analytics_network_selector_all_networks') },
                    ...networks,
                  ]}
                  onChange={this.handleChangeSelectedNetwork}
                />
              </div>
            )}
          </Bar>
          <Overview>
            <Overview.Header>
              <Overview.Header.Title>
                <Trans
                  i18nKey="network:analytics_employees_today"
                  tOptions={{ context: selectedNetwork ? 'network' : 'organisation' }}
                />
              </Overview.Header.Title>
            </Overview.Header>
            <OverviewContent>
              {loading ? <Spinner size="large" centered /> : (
                <Row>
                  <Row.StatisticsCard
                    color="#19214D"
                    title={t('network:counts_invited')}
                    tooltip={t('network:counts_invited_tooltip')}
                    count={getCount(EMetricTypes.USERS_INVITED)}
                  />
                  <Row.Divider />
                  <Row.StatisticsCard
                    color="#4CD964"
                    title={t('network:counts_registered')}
                    tooltip={t('network:counts_registered_tooltip')}
                    count={getCount(EMetricTypes.USERS_REGISTERED)}
                    description={t('network:counts_percentage_of_invited_users', {
                      count: (((
                        getCount(EMetricTypes.USERS_REGISTERED) / getCount(EMetricTypes.USERS_INVITED)) * 100
                      ) || 0).toFixed(1),
                    })}
                  />
                  <Row.Divider />
                  <Row.StatisticsCard
                    color="#F76844"
                    title={t('network:counts_pending')}
                    tooltip={t('network:counts_pending_tooltip')}
                    count={getCount(EMetricTypes.USERS_PENDING)}
                    description={t('network:counts_percentage_of_invited_users', {
                      count: (((
                        getCount(EMetricTypes.USERS_PENDING) / getCount(EMetricTypes.USERS_INVITED)) * 100
                      ) || 0).toFixed(1),
                    })}
                  />
                  <Row.Divider />
                  <Row.StatisticsCard
                    color="#FF9500"
                    title={t('network:counts_adoption_rate')}
                    tooltip={t('network:counts_adoption_rate_tooltip')}
                    formatter={(count) => `${count.toFixed(1)}%`}
                    count={((getCount(EMetricTypes.USERS_REGISTERED) / getCount(EMetricTypes.USERS_INVITED)) * 100) || 0}
                    description={t('network:counts_of_invited_users')}
                  />
                </Row>
              )}
            </OverviewContent>
          </Overview>
          <Overview>
            <Overview.Header>
              <Overview.Header.Title>
                <Trans i18nKey="network:analytics_employees_in_period" />
              </Overview.Header.Title>
              <div className="pull-right">
                <DateRangeSelector value={selectedDateValue} onChange={this.handleSetDateRange} />
              </div>
            </Overview.Header>
            {loading ? (
              <Overview.Content>
                <Spinner size="large" centered />
              </Overview.Content>
            ) : (
              <Tabs TabsHeaderItem={StatisticsTabsHeaderItem}>
                <Tabs.Item
                  title={t('network:analytics_active_employees')}
                  tooltip={t('network:analytics_active_employees_tooltip')}
                  count={getTotal(EMetricTypes.USERS_ACTIVE)}
                  oldCount={getOldTotal(EMetricTypes.USERS_ACTIVE)}
                  percentage={(getTotal(EMetricTypes.USERS_ACTIVE) / getCount(EMetricTypes.USERS_INVITED)) * 100}
                >
                  <Overview.Content>
                    <AreaChart
                      data={getTimeseries(EMetricTypes.USERS_ACTIVE)}
                      values={getValues('#5856D6')}
                      XAxisDataKey="date"
                      tint="#5856D6"
                      CustomTooltip={<ChartTooltip tooltipValueLabel={t('network:charts_count_active')} />}
                    />
                  </Overview.Content>
                </Tabs.Item>
                <Tabs.Item
                  title={t('network:analytics_adoption_rate')}
                  percentage={((getCount(EMetricTypes.USERS_REGISTERED) / getCount(EMetricTypes.USERS_INVITED)) * 100) || 0}
                >
                  <Overview.Content>
                    <AreaChart
                      data={getAdoptionTimeseries()}
                      values={getValues('#5856D6')}
                      XAxisDataKey="date"
                      tint="#5856D6"
                      CustomTooltip={<ChartTooltip
                        formatter={(count) => `${count.toFixed(1)}%`}
                        tooltipValueLabel={t('network:charts_count_adoption_rate')}
                      />}
                    />
                  </Overview.Content>
                </Tabs.Item>
              </Tabs>
            )}
          </Overview>
          <Overview>
            <Overview.Header>
              <Overview.Header.Title>
                <Trans i18nKey="network:analytics_active_employees_in_period_per_community" />
              </Overview.Header.Title>
              <div className="pull-right">
                <Select
                  clearable={false}
                  value={selectedOrder}
                  valueRenderer={({ value, label }) => (value ?
                    t('network:analytics_sort_by', { label: label.toLowerCase() }) :
                    label
                  )}
                  options={[
                    { value: false, label: t('network:analytics_sort_all') },
                    { value: EMetricTypes.USERS_REGISTERED, label: t('network:charts_adoption_rate') },
                    { value: EMetricTypes.USERS_ACTIVE, label: t('network:charts_users_active') },
                  ]}
                  onChange={this.handleChangeOrder}
                  style={{ width: 250 }}
                />
              </div>
            </Overview.Header>
            <Overview.Content>
              {loading ? <Spinner size="large" centered /> : (
                <BarChart
                  data={getNetworkNames(getGroupValues('employees.group'))}
                  XAxisDataKey="name"
                  orderKey={selectedOrder}
                  formatter={(value) => `${value.toFixed(1)}%`}
                  values={[{
                    label: t('network:charts_adoption_rate'),
                    dataKey: EMetricTypes.USERS_REGISTERED,
                    fill: '#FF9500',
                    checked: selectedBars[EMetricTypes.USERS_REGISTERED],
                  }, {
                    label: t('network:charts_users_active'),
                    dataKey: EMetricTypes.USERS_ACTIVE,
                    fill: '#5856D6',
                    checked: selectedBars[EMetricTypes.USERS_ACTIVE],
                  }]}
                  onToggleBar={this.handleToggleBar}
                />
              )}
            </Overview.Content>
          </Overview>
        </Container.Content>
      </Container>
    );
  }
}

const mapStateToProps = (state) => ({
  analytics: state.analytics,
  networks: organisationSelector.networks(state),
  permissions: state.permissions,
});

const mapDispatchToProps = {
  fetchAnalytics: require('../../actions').fetchAnalytics,
  fetchAnalyticsCounts: require('../../actions').fetchAnalyticsCounts,
};

export default withTranslation()(connect(
  mapStateToProps, mapDispatchToProps)(pageWrapper(EEventNames.VISITED_EMPLOYEES_ANALYTICS_PAGE)(StatisticsEmployeesContainer))
);
